//
// Copyright 2021 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import React, {useState} from "react";
import {makeStyles} from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import LeftArrowIcon from "@material-ui/icons/ChevronLeft";
import RightArrowIcon from "@material-ui/icons/ChevronRight";
import UpArrowIcon from "@material-ui/icons/KeyboardArrowUp";
import DownArrowIcon from "@material-ui/icons/KeyboardArrowDown";
import ClearIcon from "@material-ui/icons/Clear";
import * as Utils from "eai-configurator-ui/components/Utils";
import * as Config from "eai-configurator-ui/components/configuration/utils/ConfigurationRegistry";
import "../main.css";
import ConfigurationTable from "eai-configurator-ui/components/configuration/table/ConfigurationTable";
import {createDataIndexEditorColumn, createTextEditorColumn,} from "eai-configurator-ui/components/configuration/table/ConfigurationTableEditor";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  textField: {
    marginBottom: 0,
    marginTop: 0,
  },
  leftColumn: {
    minWidth: "300px",
  },
  rightColumn: {},
  paper: {
    padding: 10,
  },
  tableGrid: {
    overflow: "scroll",
  },
}));

const ALL_OPTIONS = "{{ALL}}";
const groupColumnPrefix = "<group>";
const TextEditor = (editorProps, value, row, column, rowIndex, columnIndex) => {
  const {
    defaultValue,
    onUpdate,
    didMount,
    className,
    autoSelectText,
    ...rest
  } = editorProps;
  const defaultValueWithouGroupPrefix = defaultValue.substring(
    groupColumnPrefix.length
  );
  const [inputValue, setInputValue] = useState(defaultValueWithouGroupPrefix);
  const handleChange = (event) => {
    setInputValue(event.target.value);
  };

  const handleBlur = (event) => {
    onUpdate(groupColumnPrefix + inputValue);
  };
  return (
    <input
      type="text"
      className={"form-control editor edit-text"}
      value={inputValue}
      autoFocus
      {...rest}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

const capitalize = (s) => {
  return s && s[0].toUpperCase() + s.slice(1);
};

const CustomizeColumnsTable = ({path, module, type, loadType, loadAllByDefault}) => {
  const classes = useStyles();

  const [allColumnNames, setAllColumnNames] = useState(null);

  const [requestPending, setRequestPending] = useState(false);

  const [forceUpdateCounter, setForceUpdateCounter] = React.useState(0);

  const [
    unselectedTableSearchVisible,
    setUnselectedTableSearchVisible,
  ] = React.useState(false);

  const [
    selectedTableSearchVisible,
    setSelectedTableSearchVisible,
  ] = React.useState(false);

  const [
    highlightedUnselectedRows,
    setHighlightedUnselectedRows,
  ] = React.useState([]);

  const [highlightedSelectedRows, setHighlightedSelectedRows] = React.useState(
    []
  );

  const ctrlPress = Utils.useKeyPress("Control");

  const shiftPress = Utils.useKeyPress("Shift");

  React.useEffect(() => {
    if (shiftPress) {
      window.getSelection().removeAllRanges();
    }
  }, [shiftPress]);

  const forceUpdate = () => {
    setForceUpdateCounter(forceUpdateCounter + 1);
  };

  let moduleColumnsPath = "";
  if (module) {
    moduleColumnsPath = path + ":moduleColumns";
    let config = Config.fromPath(moduleColumnsPath, false, true);
    let moduleConfig = config.children.find(
      (child) =>
        child.children.find((grandchild) => grandchild.key === "name").data.value === module
    );
    if (!moduleConfig) {
      moduleConfig = Config.getDefault(`${moduleColumnsPath}:module`);
      moduleConfig.children.forEach((child) => {
        if (child.key === "name") {
          child.data.value = module;
        }
        // Clear children for the column list configuration values
        child.children = [];
      });
      Config.addChild(config, moduleConfig);
    }
    moduleColumnsPath = Config.toPath(moduleConfig);
  }

  const unselectedPath = moduleColumnsPath
    ? `${moduleColumnsPath}:${type}:unselectedColumns`
    : null;

  const selectedPath = moduleColumnsPath
    ? `${moduleColumnsPath}:${type}:selectedColumns`
    : null;

  React.useEffect(() => {
    if (module) {
      fetchAvailableColumns();
    }
  }, [module]);

  React.useEffect(() => {
    if (allColumnNames) {
      // If the selected columns have not been configured then assign all of the available columns as selected
      let selectedColumnsConfig = Config.fromPath(selectedPath, false, true);
      let unselectedColumnsConfig = Config.fromPath(unselectedPath, false, true);
      if (
        loadAllByDefault &&
        selectedColumnsConfig.children.length === 0 &&
        unselectedColumnsConfig.children.length === 0
      ) {
        allColumnNames.forEach((columnName) => {
          const newItem = Config.getDefault(`${selectedPath}:column`);
          newItem.children[0].data.value = columnName;
          selectedColumnsConfig = Config.addChild(
            selectedColumnsConfig,
            newItem,
            false
          );
        });
      } else {
        const selectedColumnNames = selectedColumnsConfig.children.map(
          (child) =>
            child.children.find((grandchild) => grandchild.key === "name").data.value
        );
        const unselectedColumnNames = allColumnNames.filter((columnName) => {
          if (columnName.startsWith(groupColumnPrefix)) {
            return false;
          }
          return !selectedColumnNames.includes(columnName);
        });

        // Clear all the unselected columns from the config and recreate the children
        // This is because the REST endpoint could return more than what was previously saved
        unselectedColumnsConfig.children = [];
        unselectedColumnNames.forEach((columnName) => {
          const newItem = Config.getDefault(`${selectedPath}:column`);
          newItem.children[0].data.value = columnName;
          unselectedColumnsConfig = Config.addChild(
            unselectedColumnsConfig,
            newItem,
            false
          );
        });
      }
      setHighlightedSelectedRows([]);
      setHighlightedUnselectedRows([]);
    }
  }, [allColumnNames]);

  const fetchAvailableColumns = () => {
    if (!requestPending) {
      setRequestPending(true);

      let url = `loader/module${capitalize(loadType)}?module=${module}`;
      Utils.fetchData(url, {
        method: "GET",
      })
        .then((data) => {
          setRequestPending(false);

          const columns = [];
          columns.push(ALL_OPTIONS);
          let previousGroupName = null;
          data.forEach((column) => {
            if (column.group !== previousGroupName && column.group) {
              // New group detected
              previousGroupName = column.group;
              columns.push(groupColumnPrefix + column.group);
            }
            columns.push(column.column);
          });
          setAllColumnNames(columns);
        })
        .catch((error) => {
          setRequestPending(false);
        });
    }
  };

  const selectedTableRowEvents = {
    onDoubleClick: (e, row, rowIndex) => {
      moveColumns(
        selectedPath,
        unselectedPath,
        [row.dataIndex],
        setHighlightedSelectedRows,
        highlightedUnselectedRows,
        setHighlightedUnselectedRows
      );
    },
  };
  const unselectedTableRowEvents = {
    onDoubleClick: (e, row, rowIndex) => {
      moveColumns(
        unselectedPath,
        selectedPath,
        [row.dataIndex],
        setHighlightedUnselectedRows,
        highlightedSelectedRows,
        setHighlightedSelectedRows
      );
    },
  };
  const handleDeleteItem = (row, rowIndex) => {
    if (row) {
      Config.deleteChild(selectedPath, row.dataIndex);
      forceUpdate();
    }
  };

  const getAllOptionsRow = () => {
    return (
      <Tooltip title={"All Future UDFs"}>
        <strong style={{color: "#f11827"}}>{ALL_OPTIONS}</strong>
      </Tooltip>
    );
  };

  const unselectedTableColumns = unselectedPath
    ? [
      createTextEditorColumn(
        unselectedPath + ":column:name",
        unselectedTableSearchVisible,
        {
          style: {cursor: "default"},

          formatter: (cell, row, rowIndex) => {
            if (cell.startsWith(ALL_OPTIONS)) {
              return getAllOptionsRow();
            }
            return cell;
          },
        }
      ),
      createDataIndexEditorColumn(),
    ]
    : [];

  const selectedTableColumns = selectedPath
    ? [
      createTextEditorColumn(
        selectedPath + ":column:name",
        selectedTableSearchVisible,
        {
          style: {cursor: "default"},
          formatter: (cell, row, rowIndex, {handleDeleteItem}) => {
            if (cell.startsWith(groupColumnPrefix)) {
              const name = cell.substr(groupColumnPrefix.length);
              return (
                <span class="separator">
                    <strong style={{color: "#c5c5c5"}}>{name}</strong>
                    <Tooltip title={"Delete Group"}>
                      <IconButton
                        onClick={(event) => {
                          handleDeleteItem(row, rowIndex);
                          event.stopPropagation();
                        }}
                      >
                        <ClearIcon/>
                      </IconButton>
                    </Tooltip>
                  </span>
              );
            } else if (cell.startsWith(ALL_OPTIONS)) {
              return getAllOptionsRow();
            }
            return cell;
          },
          formatExtraData: {
            handleDeleteItem: handleDeleteItem,
          },
          editable: (content, row, rowIndex, columnIndex) =>
            content.startsWith(groupColumnPrefix),
          editorRenderer: (props) => <TextEditor {...props} />,
        }
      ),
      createDataIndexEditorColumn(),
    ]
    : [];

  const highlightRow = (highlightedRows, row, isSelect, rowIndex, e) => {
    const newHighlightedRows =
      ctrlPress || shiftPress ? [...highlightedRows] : [];
    if (isSelect) {
      newHighlightedRows.push(row.dataIndex);
      if (shiftPress) {
        document.getSelection().removeAllRanges();

        // Sort so that lowest index is first
        newHighlightedRows.sort((a, b) => a - b);

        const fromIndex = newHighlightedRows[0];
        const toIndex = newHighlightedRows[newHighlightedRows.length - 1];
        if (fromIndex !== toIndex) {
          for (let i = fromIndex + 1; i < toIndex; i++) {
            if (!newHighlightedRows.includes(i)) {
              newHighlightedRows.push(i);
            }
          }
        }
      }
    } else {
      const index = newHighlightedRows.indexOf(row.dataIndex);
      if (index >= 0) {
        newHighlightedRows.splice(index, 1);
      }
    }
    return newHighlightedRows;
  };
  const handleCellEdited = (oldValue, newValue, row, column) => {
    Config.setDataValue(
      `${path}:column[${row.dataIndex}]${column.dataField}`,
      newValue
    );
  };

  const handleUnselectedRowHighlighted = (row, isSelect, rowIndex, e) => {
    setHighlightedUnselectedRows(
      highlightRow(highlightedUnselectedRows, row, isSelect, row.dataIndex, e)
    );
  };
  const handleSelectedRowHighlighted = (row, isSelect, rowIndex, e) => {
    setHighlightedSelectedRows(
      highlightRow(highlightedSelectedRows, row, isSelect, row.dataIndex, e)
    );
  };

  const handleLeftArrowClick = () => {
    moveColumns(
      selectedPath,
      unselectedPath,
      highlightedSelectedRows,
      setHighlightedSelectedRows,
      highlightedUnselectedRows,
      setHighlightedUnselectedRows
    );
  };

  const handleRightArrowClick = () => {
    moveColumns(
      unselectedPath,
      selectedPath,
      highlightedUnselectedRows,
      setHighlightedUnselectedRows,
      highlightedSelectedRows,
      setHighlightedSelectedRows
    );
  };

  const moveColumns = (
    fromPath,
    toPath,
    fromHighlightedRows,
    setFromHighlightedRows,
    toHighlightedRows,
    setToHighlightedRows
  ) => {
    const rows = fromHighlightedRows.filter((rowIndex) => {
      const row = Config.fromPath(`${fromPath}:column[${rowIndex}]:name`).data
        .value;
      return !row.startsWith(groupColumnPrefix);
    });

    // Sort so that highest index is first
    rows.sort((a, b) => b - a);

    rows.forEach((rowIndex) => {
      const toMove = Config.fromPath(`${fromPath}:column[${rowIndex}]`);
      Config.addChild(Config.fromPath(toPath, false, true), toMove, true);
      Config.deleteChild(fromPath, rowIndex);
      setFromHighlightedRows([]);
    });

    const newHighlightedRows = [];
    rows.forEach((row, index) => {
      if (!newHighlightedRows.includes(index)) {
        newHighlightedRows.push(index);
      }
    });

    // Some new highlighted rows were added at the front so offset all existing highlighted indexes by "rows.length" (number of newly added rows) to keep them highlighted
    toHighlightedRows.forEach((rowIndex) => {
      newHighlightedRows.push(rowIndex + rows.length);
    });
    setToHighlightedRows(newHighlightedRows);
  };

  const shiftRowsBy = (rows, rowIndexDelta) => {
    let newHighlightedRows = [];
    for (let i = 0; i < rows.length; i++) {
      const rowIndex = rows[i];
      const swapped = Config.swapChildren(
        Config.fromPath(selectedPath),
        rowIndex + rowIndexDelta,
        rowIndex
      );

      // If the first row cannot be shifted then abort and do not shift anything (it likely means that the row is at the edge of the list)
      if (!swapped && i === 0) {
        return;
      }
      newHighlightedRows.push(swapped ? rowIndex + rowIndexDelta : rowIndex);
    }
    setHighlightedSelectedRows(newHighlightedRows);
  };

  const handleUpArrowClick = () => {
    const rows = highlightedSelectedRows;

    // Sort so that lowest index is first
    rows.sort((a, b) => a - b);
    shiftRowsBy(rows, -1);
  };

  const handleDownArrowClick = () => {
    const rows = highlightedSelectedRows;

    // Sort so that highest index is first
    rows.sort((a, b) => b - a);
    shiftRowsBy(rows, 1);
  };

  const highlightUnselectedRow = {
    mode: "checkbox",
    clickToSelect: true,
    bgColor: "#EEEEEE",
    selected: highlightedUnselectedRows,
    onSelect: handleUnselectedRowHighlighted,
    hideSelectColumn: true,
  };
  const highlightSelectedRow = {
    mode: "checkbox",
    bgColor: "#EEEEEE",
    selected: highlightedSelectedRows,
    clickToSelect: true,
    clickToEdit: true,
    onSelect: handleSelectedRowHighlighted,
    hideSelectColumn: true,
  };
  if (!moduleColumnsPath) {
    return null;
  }
  return (
    <Grid
      container
      spacing={1}
      justify="center"
      alignItems="center"
      className={classes.filterGrid}
      style={{height: "100%"}}
    >
      <Grid item xs={5} style={{overflow: "auto", height: "650px"}}>
        <ConfigurationTable
          path={unselectedPath}
          childKey={"column"}
          columns={unselectedTableColumns}
          rowEvents={unselectedTableRowEvents}
          pageSize={1000}
          headerClasses="header-class"
          selectRow={highlightUnselectedRow}
          noDataIndication={"Empty"}
          hideAddButton
          handleToggleSearchVisibility={() =>
            setUnselectedTableSearchVisible(!unselectedTableSearchVisible)
          }
        />
      </Grid>
      <Grid xs={1} item>
        <Grid container direction="column" alignItems="center">
          <IconButton
            onClick={handleRightArrowClick}
            disabled={highlightedUnselectedRows.length === 0}
          >
            <RightArrowIcon/>
          </IconButton>
          <IconButton
            onClick={handleLeftArrowClick}
            disabled={highlightedSelectedRows.length === 0}
          >
            <LeftArrowIcon/>
          </IconButton>
        </Grid>
      </Grid>
      <Grid item xs={5} style={{overflow: "auto", height: "650px"}}>
        <ConfigurationTable
          path={selectedPath}
          childKey={"column"}
          columns={selectedTableColumns}
          rowEvents={selectedTableRowEvents}
          pageSize={1000}
          headerClasses="header-class"
          selectRow={highlightSelectedRow}
          noDataIndication={"Empty"}
          handleToggleSearchVisibility={() =>
            setSelectedTableSearchVisible(!selectedTableSearchVisible)
          }
          handleNewItemAdded={(newItem) => {
            newItem.children.find((child) => child.key === "name").data.value =
              groupColumnPrefix + "Untitled Group";
          }}
          addButtonTooltip={"Add Group"}
        />
      </Grid>
      <Grid xs={1} item>
        <Grid container spacing={0} direction="column" alignItems="center">
          <IconButton
            onClick={handleUpArrowClick}
            disabled={highlightedSelectedRows.length === 0}
          >
            <UpArrowIcon/>
          </IconButton>
          <IconButton
            onClick={handleDownArrowClick}
            disabled={highlightedSelectedRows.length === 0}
          >
            <DownArrowIcon/>
          </IconButton>
        </Grid>
      </Grid>
    </Grid>
  );
};
export default CustomizeColumnsTable;
